import {
  ChangeDetectionStrategy,
  Component,
  inject,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialog,
} from '@angular/material/dialog';
import { CustodianInfoAddEditComponent } from '../custodian-info-add-edit/custodian-info-add-edit.component';
import { NgTemplateOutlet } from '@angular/common';
import { Observable } from 'rxjs';
import { PresentationalAtomsModule } from '@et/presentational/atoms';

export type CustodianInfoDetailsComponentData = {
  eJournalCustodianEmail: string;
  eJournalCustodianName: string;
  editFn: () => void;
  updateFn: (name: string, email: string) => Observable<void>;
};

@Component({
  selector: 'et-journal-custodian-info-details',
  imports: [MatDialogClose, NgTemplateOutlet, PresentationalAtomsModule],
  templateUrl: './custodian-info-details.component.html',
  styleUrl: './custodian-info-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustodianInfoDetailsComponent {
  private readonly dialogRef = inject(
    MatDialogRef<CustodianInfoAddEditComponent>,
  );
  private readonly dialogData =
    inject<CustodianInfoDetailsComponentData>(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);

  deleteConrimationTemplate = viewChild.required<TemplateRef<unknown>>(
    'deleteConrimationTemplate',
  );

  /** Custodian name and email supplied via dialog data for display. */
  get custodianInfo() {
    return {
      eJournalCustodianEmail: this.dialogData.eJournalCustodianEmail,
      eJournalCustodianName: this.dialogData.eJournalCustodianName,
    };
  }

  /**
   * Closes this dialog and opens the add/edit custodian dialog via the injected `editFn`.
   */
  onEdit() {
    this.dialogRef.close();
    this.dialogRef.afterClosed().subscribe(() => this.dialogData.editFn());
  }

  /**
   * Closes this dialog, then opens the delete-confirmation modal after the close animation completes.
   */
  onDelete() {
    this.dialogRef.close();
    this.dialogRef.afterClosed().subscribe(() => {
      this.dialog.open(this.deleteConrimationTemplate(), {
        width: '100%',
        maxWidth: '500px',
        panelClass: ['et-dialog-radius', 'et-dialog-padding'],
        autoFocus: false,
        id: 'delete-custodian-info-modal',
      });
    });
  }

  /**
   * Clears custodian fields on the profile via `updateFn` and closes the delete-confirmation modal on success.
   */
  deleteCustodianInfo() {
    this.dialogData
      .updateFn('', '')
      .subscribe(() =>
        this.dialog.getDialogById('delete-custodian-info-modal')?.close(),
      );
  }
}
